import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import type { AnalyticsRuleWeakness } from '@proref/types';

// The cohort weakness map from the design: one cell per rule article, coloured by federation-wide
// accuracy (red < 60%, amber 60–75%, green ≥ 75%). Untouched rules (no attempts) render neutral so
// "hard" and "unseen" never look alike. Colours are token-mixed for palette-awareness.
//
// Built on Recharts (the app's chart library, §2) rather than a plain CSS grid: each rule is a point
// on a hidden numeric col/row axis, drawn as a square via a custom `shape`. The grid's pixel width is
// measured directly with a ResizeObserver instead of ResponsiveContainer, because Recharts needs an
// explicit width/height to render anything at all — ResponsiveContainer reports 0 under jsdom and
// renders nothing there, which would make this component untestable (see RefereeTrendChart for the
// same constraint). The fallback width keeps it rendering (at a fixed size) wherever ResizeObserver
// is unavailable, tests included.

interface RuleWeaknessHeatmapProps {
  rules: AnalyticsRuleWeakness[];
}

const COLS = 6;
const GAP = 6;
const CELL_RADIUS = 6;
const FALLBACK_WIDTH = 480;

function cellBg(accuracy: number | null): string {
  if (accuracy == null) return 'var(--surface-2)'; // untouched — neutral, not "bad"
  if (accuracy < 0.6) return 'color-mix(in oklch, var(--danger) 55%, var(--surface))';
  if (accuracy < 0.75) return 'color-mix(in oklch, var(--warn) 50%, var(--surface))';
  return 'color-mix(in oklch, var(--success) 35%, var(--surface))';
}

// Tracks the wrapping div's content width so the grid sizes to the card's real width in the browser.
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

interface CellPoint {
  x: number;
  y: number;
  size: number;
  rule: AnalyticsRuleWeakness;
}

interface CellShapeProps {
  cx?: number;
  cy?: number;
  payload?: CellPoint;
}

function HeatmapCell({ cx, cy, payload }: CellShapeProps) {
  if (cx == null || cy == null || !payload) return null;
  const { rule, size } = payload;
  const half = size / 2;
  return (
    <g>
      <rect
        x={cx - half}
        y={cy - half}
        width={size}
        height={size}
        rx={CELL_RADIUS}
        fill={cellBg(rule.accuracy)}
        stroke="var(--line)"
      />
      <text x={cx - half + 8} y={cy - half + 16} fontFamily="var(--mono)" fontSize="11" fontWeight="700" fill="var(--ink)">
        {rule.article_number}
      </text>
      <text x={cx - half + 8} y={cy + half - 10} fontFamily="var(--mono)" fontSize="13" fontWeight="700" fill="var(--ink)">
        {rule.accuracy == null ? '—' : Math.round(rule.accuracy * 100) + '%'}
      </text>
    </g>
  );
}

interface CellTooltipEntry {
  payload?: CellPoint;
}
interface CellTooltipProps {
  active?: boolean;
  payload?: CellTooltipEntry[];
}

function CellTooltip({ active, payload }: CellTooltipProps) {
  const { t } = useTranslation();
  const rule = payload?.[0]?.payload?.rule;
  if (!active || !rule) return null;
  return (
    <div className="rounded border border-line bg-surface px-3 py-2 text-[11.5px] shadow-1">
      <div className="font-medium text-ink">
        {t('admin.analytics.ruleWeakness.tooltip', {
          title: rule.title,
          accuracy: rule.accuracy == null ? t('admin.analytics.ruleWeakness.noAttempts') : Math.round(rule.accuracy * 100) + '%',
        })}
      </div>
    </div>
  );
}

export function RuleWeaknessHeatmap({ rules }: RuleWeaknessHeatmapProps) {
  const { t } = useTranslation();
  const [containerRef, width] = useMeasuredWidth();

  if (rules.length === 0) {
    return <p className="text-sm text-muted">{t('admin.analytics.ruleWeakness.noDataYet')}</p>;
  }

  const rows = Math.ceil(rules.length / COLS);
  const cellSize = width / COLS;
  const height = rows * cellSize;
  const rectSize = Math.max(cellSize - GAP, 0);

  // Row 0 renders at the top: plotted from the bottom row up so the y-domain itself stays a plain
  // increasing scale (no `reversed` flag to second-guess) while still reading top-to-bottom.
  const points: CellPoint[] = rules.map((rule, i) => ({
    x: (i % COLS) + 0.5,
    y: rows - 1 - Math.floor(i / COLS) + 0.5,
    size: rectSize,
    rule,
  }));

  return (
    // min-w-0 + overflow-hidden: without them, this div's grid-item ancestor (Card) inherits a
    // min-width from the SVG's own fixed pixel width — once widened, the grid column can never
    // shrink back below that, so the chart only ever grows. min-w-0 breaks that propagation;
    // overflow-hidden covers the one-frame gap before the next measurement catches up.
    <div ref={containerRef} className="min-w-0 overflow-hidden">
      <ScatterChart width={width} height={height} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <XAxis type="number" dataKey="x" domain={[0, COLS]} hide />
        <YAxis type="number" dataKey="y" domain={[0, rows]} hide />
        <Tooltip content={<CellTooltip />} cursor={false} wrapperStyle={{ outline: 'none' }} />
        <Scatter data={points} shape={HeatmapCell} isAnimationActive={false} />
      </ScatterChart>

      {/* legend */}
      <div className="mt-3.5 flex items-center gap-3.5 text-[11px] text-muted">
        <LegendSwatch color="color-mix(in oklch, var(--danger) 55%, var(--surface))" label={t('admin.analytics.ruleWeakness.legendLow')} />
        <LegendSwatch color="color-mix(in oklch, var(--warn) 50%, var(--surface))" label={t('admin.analytics.ruleWeakness.legendMid')} />
        <LegendSwatch color="color-mix(in oklch, var(--success) 35%, var(--surface))" label={t('admin.analytics.ruleWeakness.legendHigh')} />
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}
